// Iter114 — Cycle Tracker overhaul (Phase 7E Cycle Tracker UX).
//
// Iter114.2 — Live-spot automation pass:
//   - Cycle page auto-fetches live spot for every active cycle from
//     the broker quote endpoint (`GET /cycles/{id}/live-spot`) on
//     mount, Dashboard-style.  Operator no longer types or saves
//     spot manually.
//   - `RecoveryPanel` rendered as a pure read-only summary card —
//     no inputs, no Save button, no Pull-live button.
//   - Cost-basis chart plots a CYAN "you are here" dot for the
//     live spot when it sits inside the auto-scaled Y band; an
//     off-chart chip appears above the chart otherwise.
//   - Recovery / unrealized P/L metrics are recomputed client-side
//     from the live spot so they refresh instantly without a
//     persistence round-trip.
//   - `POST /cycles/{id}/spot` endpoint remains dormant on the
//     backend for protection-test backwards compatibility (NOT
//     called by the UI).
//
// Iter114.3 — "The Wound" projected cycle P/L:
//   - Adds a Wound section inside `RecoveryPanel` that surfaces the
//     full cycle P/L if the currently-open CC gets assigned.
//   - Formula (operator-defined):
//         wound_per_share = credits_received_total + (Kc_open − Kp)
//                         = Kc_open − cost_basis_live  (equivalent)
//         wound_dollars   = wound_per_share × 100 (per 1 contract)
//   - Renders only when `status === "CC_OPEN"` (no Kc to project
//     against in CSP_OPEN / AWAITING_CC / CLOSED).
//   - Footer reminds operator the math is per 1 contract.
//
// Iter114.4 — What-if Wound scenario launcher:
//   - Adds a "Try a different strike · live chain" button below
//     the Wound block.  Click opens the shared Dashboard
//     `CCChainDiagModal` pre-filled with this cycle's context and
//     auto-fetches the live option chain from the existing
//     `/api/chain-diag/cc` endpoint.
//   - Operator-facing math: the modal's `pl_cycle` column equals
//     the hypothetical Wound per share when
//     `p_prev = credits_received_total − open_cc_premium` (we
//     strip the open leg's premium so each strike represents a
//     candidate replacement, not an addition).
//   - Zero new endpoints, zero persistence, zero broker order
//     placement.  Pure read-only scenario exploration.
//
// Iter114.5 — What-if Wound improvement highlighter:
//   - Passes `currentWoundPerShare = woundPerShare` into the
//     CCChainDiagModal so each chain row gets a delta badge
//     (`↑ +$3.13` / `↓ -$1.20`) comparing the candidate's
//     hypothetical Wound vs. the operator's current Wound.
//   - Beats-current rows get a purple left-border highlight + a
//     `BEAT` chip next to the strike so the operator can scan
//     the entire chain in 2 seconds.
//
// Iter114.6 — Tradeable opportunity filter (decision-support
// reframing of Iter114.5):
//   - Replaces the "Only improvements" toggle with a "Tradeable
//     only" toggle that combines THREE hard gates:
//       1. Strike ∈ [spot − 2σ, spot + 2σ] — tradeable range.
//       2. Wheel ITM doctrine `passes_all && liq_pass`.
//       3. Beats current Wound (only enforced when in CC_OPEN).
//   - Launcher button now ALSO renders in AWAITING_CC.
//
// Iter114.7 — Order ticket preview:
//   - Threads `kc_open` into the chain modal so it can look up
//     the operator's current leg from the same chain payload.
//   - Surfaces an inline "→ Net roll +$X.XX/sh" badge under
//     every SELECT / VALID verdict, computed as candidate mid
//     minus current-leg mid (CC_OPEN) or just candidate mid
//     (AWAITING_CC).
//
// Iter114.8 — Multi-contract support:
//   - Adds a `contracts` field to NewCycleModal (default 1) +
//     persists it backend-side; `_enrich_cycle` defaults
//     existing docs to 1 for back-compat.
//   - Wound dollar total auto-scales: `woundPerShare × 100 ×
//     contracts`.  Per-1-contract footer reminder replaced with
//     the actual "Per N contracts (N×100 shares)" indicator.
//   - Threads `contracts` into the chain modal so every Net Roll
//     / Open ticket surfaces a per-share AND $-total scaled to
//     the operator's real position size.
//
// Iter114.9 — CC Chain modal layout overhaul:
//   - Modal width expands from `max-w-5xl` → `max-w-[1600px]`
//     (height `max-h-[85vh]` → `max-h-[92vh]`) so the chain
//     table, heatmap and pill row fit without clipping.
//   - Order ticket moves out of the Verdict cell into a DEDICATED
//     "TICKET" column with its own header.  Per-share fill +
//     dollar total stack cleanly in the new column; REJECT rows
//     show a muted "—" placeholder.
//
// Iter114.10 — Portfolio summary strip:
//   - 4-tile bento strip at the top of `/log` aggregating across
//     ALL active cycles: Active count, Capital invested, Live
//     unrealized P/L, and Projected Wound (with "N of M CCs
//     open" subtitle).
//   - Pure client-side aggregation over `cycles` + `liveSpots`
//     already on hand.  Zero new endpoints.
//
// Read-only doctrine survives: every persistence call here is to the
// existing `/cycles*` endpoint family.  No journal write.  No broker
// order placement.

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, BookOpen, Plus, X, Trash2, RefreshCw, RotateCcw,
  CheckCircle2, Activity, AlertTriangle, TrendingUp,
  Crosshair, FlaskConical,
} from "lucide-react";
import { API, AddCCModal, TickerLogo, CCChainDiagModal } from "@/App";
import { useI18n } from "@/i18n";
import FreshnessIndicator from "@/components/FreshnessIndicator";
import { SkeletonList } from "@/components/Skeleton";
import { toast } from "sonner";

function round2(v) { return Math.round((v || 0) * 100) / 100; }
function fmtMoney(v) { return v == null ? "—" : `$${round2(v).toFixed(2)}`; }
function fmtPct(v) { return v == null ? "—" : `${round2(v).toFixed(2)}%`; }
function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : "—";
}
function daysBetween(a, b) {
  if (!a) return 0;
  const start = new Date(a).getTime();
  const end = (b ? new Date(b).getTime() : Date.now());
  return Math.max(0, Math.round((end - start) / 86_400_000));
}

const STATUS_PALETTE = {
  CSP_OPEN: { fg: "#FFB800", label: "CSP Open" },
  AWAITING_CC: { fg: "#22D3FF", label: "Awaiting CC" },
  CC_OPEN: { fg: "#00FF88", label: "CC Open" },
  CLOSED: { fg: "#8A8A93", label: "Closed" },
};

const LEG_PALETTE = {
  open: "#22D3FF",
  expired: "#FFB800",
  assigned: "#00FF88",
  rolled: "#A78BFA",
};

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────
export default function CycleTrackerPage() {
  const { t } = useI18n();
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ccModal, setCcModal] = useState(null);
  const [rollModal, setRollModal] = useState(null);
  const [newCycleOpen, setNewCycleOpen] = useState(false);
  // Iter114.2 — Live-spot map keyed by cycle id.  Auto-fetched once
  // after each `/cycles` reload via the broker-pull endpoint that
  // the Dashboard already uses.  Pure read-only — no persistence.
  const [liveSpots, setLiveSpots] = useState({});
  const [liveSpotsAt, setLiveSpotsAt] = useState(null);

  const fetchLiveSpotsFor = useCallback(async (activeCycles) => {
    if (!activeCycles?.length) { setLiveSpots({}); return; }
    const results = await Promise.allSettled(
      activeCycles.map(c => axios.get(`${API}/cycles/${c.id}/live-spot`))
    );
    const map = {};
    results.forEach((r, i) => {
      const v = r.status === "fulfilled" ? r.value?.data?.current_spot : null;
      if (v != null) map[activeCycles[i].id] = v;
    });
    setLiveSpots(map);
    setLiveSpotsAt(Date.now());
  }, []);

  const fetchCycles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cycles`);
      const list = Array.isArray(r.data) ? r.data : [];
      setCycles(list);
      // Auto-pull live spots only for ACTIVE cycles (closed ones
      // don't need a recovery panel and we don't want to hammer the
      // broker quote endpoint).
      const active = list.filter(c => c.status !== "CLOSED");
      fetchLiveSpotsFor(active);
    } catch { /* ignore */ }
    setLoading(false);
  }, [fetchLiveSpotsFor]);
  useEffect(() => { fetchCycles(); }, [fetchCycles]);

  const refreshSpots = useCallback(() => {
    const active = cycles.filter(c => c.status !== "CLOSED");
    fetchLiveSpotsFor(active);
  }, [cycles, fetchLiveSpotsFor]);

  const doAction = async (id, action) => {
    try {
      await axios.post(`${API}/cycles/${id}/action`, { action });
      fetchCycles();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Action "${action}" failed`);
    }
  };
  const deleteCycle = async (id) => {
    if (!window.confirm("Delete this cycle?")) return;
    try { await axios.delete(`${API}/cycles/${id}`); fetchCycles(); } catch { /* ignore */ }
  };

  const open = cycles.filter(c => c.status !== "CLOSED");
  const closed = cycles.filter(c => c.status === "CLOSED");

  // Iter114.10 — Portfolio summary aggregation.  Pure client-side
  // sum over the active-cycle window.  No new endpoint; uses the
  // same enriched payload + live-spots map already on hand.
  //   - Capital invested  = Σ `c.capital_invested` (already scaled
  //     by `contracts` server-side, Iter114.8).
  //   - Live unrealized   = Σ `(liveSpot − cost_basis_live) ×
  //     100 × contracts` for cycles holding shares (AWAITING_CC /
  //     CC_OPEN) where a live broker quote is available.
  //   - Projected Wound   = Σ `wound_per_share × 100 ×
  //     contracts` for cycles in `CC_OPEN`.
  const portfolioSummary = (() => {
    let totalCapital = 0;
    let totalUnrealized = 0;
    let totalUnrealizedHasData = false;
    let totalWound = 0;
    let woundCount = 0;
    for (const c of open) {
      const contracts = Math.max(1, parseInt(c.contracts) || 1);
      totalCapital += (c.capital_invested || 0);
      const sharesHeld = c.status === "AWAITING_CC" || c.status === "CC_OPEN";
      const liveSpot = liveSpots[c.id];
      if (sharesHeld && liveSpot != null && c.cost_basis_live != null) {
        totalUnrealized += (liveSpot - c.cost_basis_live) * 100 * contracts;
        totalUnrealizedHasData = true;
      }
      if (c.status === "CC_OPEN" && Array.isArray(c.cc_legs)) {
        const openLeg = c.cc_legs.find(l => (l.outcome || "open") === "open");
        if (openLeg && c.kp > 0 && c.credits_received_total != null) {
          const wound = c.credits_received_total + (openLeg.kc - c.kp);
          totalWound += wound * 100 * contracts;
          woundCount++;
        }
      }
    }
    return {
      count: open.length,
      capital: totalCapital,
      unrealized: totalUnrealized,
      unrealizedHasData: totalUnrealizedHasData,
      wound: totalWound,
      woundCount,
    };
  })();

  return (
    <div data-testid="cycle-tracker-page" className="animate-fade-in">
      {/* Hero header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-black tracking-tighter text-white font-['Chivo']">
            {t("page.cycle_tracker") || "Cycle Tracker"}
          </h2>
          <p className="text-sm text-[#8A8A93] mt-1">
            CSP → Assignment → CC → Roll → Close · cost-basis evolution graph · capital-to-recover signals
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Iter114.2 / Iter117.4 — Live-spot freshness now powered by
              shared <FreshnessIndicator /> so the cockpit's "is this
              data fresh?" language stays identical across pages. */}
          <FreshnessIndicator
            fetchedAt={liveSpotsAt}
            onRefresh={refreshSpots}
            loading={false}
            testidPrefix="cycle-tracker-spots"
            refreshTitle="Refresh live spots from broker"
          />
          <button
            type="button"
            data-testid="cycle-tracker-new-cycle"
            onClick={() => setNewCycleOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-[#00FF88] text-[#08080A] text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-[#00FF88]/90"
          >
            <Plus size={13} /> New Cycle
          </button>
        </div>
      </div>

      {/* Iter114.10 — Portfolio summary strip.  Single-glance
          aggregate across all active cycles.  Hidden when there
          are no active cycles to keep the empty-state clean. */}
      {open.length > 0 && (
        <div
          data-testid="portfolio-summary-strip"
          className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 border border-[#26262B] rounded-sm overflow-hidden"
        >
          <div className="px-4 py-3 bg-[#0A0A0D]">
            <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#8A8A93] mb-1">Active cycles</div>
            <div data-testid="portfolio-summary-count" className="text-2xl font-mono font-bold text-white tabular-nums">
              {portfolioSummary.count}
            </div>
          </div>
          <div className="px-4 py-3 bg-[#0A0A0D] border-l border-[#26262B]">
            <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#8A8A93] mb-1">Capital invested</div>
            <div data-testid="portfolio-summary-capital" className="text-2xl font-mono font-bold text-[#E5E5E7] tabular-nums">
              ${portfolioSummary.capital.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="px-4 py-3 bg-[#0A0A0D] border-l border-[#26262B]">
            <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#8A8A93] mb-1">Live unrealized P/L</div>
            <div
              data-testid="portfolio-summary-unrealized"
              className={`text-2xl font-mono font-bold tabular-nums ${
                !portfolioSummary.unrealizedHasData
                  ? "text-[#5F5F66]"
                  : portfolioSummary.unrealized >= 0
                    ? "text-[#00FF88]"
                    : "text-[#FF5577]"
              }`}
            >
              {!portfolioSummary.unrealizedHasData
                ? "—"
                : `${portfolioSummary.unrealized >= 0 ? "+" : ""}$${Math.round(portfolioSummary.unrealized).toLocaleString('en-US')}`}
            </div>
          </div>
          <div className="px-4 py-3 bg-[#0A0A0D] border-l border-[#26262B]">
            <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#A78BFA] mb-1 inline-flex items-center gap-1.5">
              <AlertTriangle size={9} /> Projected Wound
            </div>
            <div
              data-testid="portfolio-summary-wound"
              className={`text-2xl font-mono font-bold tabular-nums ${
                portfolioSummary.woundCount === 0
                  ? "text-[#5F5F66]"
                  : portfolioSummary.wound >= 0
                    ? "text-[#00FF88]"
                    : "text-[#FF5577]"
              }`}
            >
              {portfolioSummary.woundCount === 0
                ? "—"
                : `${portfolioSummary.wound >= 0 ? "+" : ""}$${Math.round(portfolioSummary.wound).toLocaleString('en-US')}`}
            </div>
            {portfolioSummary.woundCount > 0 && (
              <div className="text-[9px] font-mono text-[#5F5F66] mt-0.5">
                across {portfolioSummary.woundCount}/{portfolioSummary.count} CC{portfolioSummary.woundCount === 1 ? "" : "s"} open
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        /* Iter119.18 — Skeleton cycle cards instead of a lonely
           centered Loader2. Same visual rhythm as the real
           CycleCards so the page doesn't jump on load. */
        <SkeletonList count={2} testid="cycle-tracker-skeleton" />
      ) : (
        <>
          <div className="mb-8">
            <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-[#00FF88] mb-4 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
              Active ({open.length})
            </h3>
            {open.length === 0 ? (
              <div
                data-testid="cycle-tracker-empty-active"
                className="bg-[#121216] border border-[#26262B] rounded-md p-10 text-center"
              >
                <BookOpen size={32} className="text-[#8A8A93] mx-auto mb-3" />
                <p className="text-[#8A8A93] text-sm mb-3">No active cycles</p>
                <button
                  type="button"
                  onClick={() => setNewCycleOpen(true)}
                  className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#00FF88] border border-[#00FF88]/30 hover:border-[#00FF88]/60 px-3 py-1.5 rounded-sm"
                >
                  + Open one manually
                </button>
              </div>
            ) : open.map(c => (
              <CycleCard
                key={c.id}
                c={c}
                liveSpot={liveSpots[c.id]}
                liveSpotsAt={liveSpotsAt}
                onAction={doAction}
                onAddCC={() => setCcModal(c.id)}
                onRoll={() => setRollModal(c.id)}
                onDelete={deleteCycle}
                onRefresh={fetchCycles}
              />
            ))}
          </div>
          {closed.length > 0 && (
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-[#8A8A93] mb-4">
                Closed ({closed.length})
              </h3>
              {closed.map(c => (
                <CycleCard
                  key={c.id}
                  c={c}
                  onAction={doAction}
                  onDelete={deleteCycle}
                  onRefresh={fetchCycles}
                />
              ))}
            </div>
          )}
        </>
      )}

      {ccModal && (
        <AddCCModal
          cycleId={ccModal}
          onClose={() => setCcModal(null)}
          onSaved={fetchCycles}
        />
      )}
      {rollModal && (
        <RollCCModal
          cycleId={rollModal}
          onClose={() => setRollModal(null)}
          onSaved={fetchCycles}
        />
      )}
      {newCycleOpen && (
        <NewCycleModal
          onClose={() => setNewCycleOpen(false)}
          onSaved={fetchCycles}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Cycle Card (full redesign)
// ────────────────────────────────────────────────────────────────────
function CycleCard({ c, liveSpot, liveSpotsAt, onAction, onAddCC, onRoll, onDelete, onRefresh }) {
  const isClosed = c.status === "CLOSED";
  const palette = STATUS_PALETTE[c.status] || STATUS_PALETTE.CLOSED;
  const kp = c.kp || 0;
  const pp = c.pp || 0;
  const creditsTotal = c.credits_received_total ?? round2(pp);
  const costBasisLive = c.cost_basis_live;
  // Iter114.2 — Live spot from broker quote endpoint takes priority
  // over the (legacy) operator-saved `c.current_spot`.  Backend
  // continues to derive recovery metrics from the persisted spot —
  // we override with client-side derivation whenever live data is
  // present so the UI always reflects the freshest quote.
  const currentSpot = liveSpot != null ? liveSpot : c.current_spot ?? null;
  const sharesHeld = c.status === "AWAITING_CC" || c.status === "CC_OPEN";
  const liveDerived = currentSpot != null && costBasisLive != null;
  const plUnrealized = liveDerived && sharesHeld
    ? round2(currentSpot - costBasisLive)
    : c.pl_unrealized_per_share;
  const capitalToRecover = liveDerived
    ? Math.max(0, round2(costBasisLive - currentSpot))
    : c.capital_to_recover_per_share;
  const recoveryPct = liveDerived && currentSpot > 0
    ? round2(Math.max(0, ((costBasisLive - currentSpot) / currentSpot) * 100))
    : c.recovery_pct_to_breakeven;
  const plCycle = c.pl_cycle;
  const roiCycle = c.roi_cycle;
  const daysIn = daysBetween(c.created_at, c.closed_at);
  const ccLegs = c.cc_legs || [];
  const events = c.event_history || [];

  // Iter114.3 — "The Wound" — projected total cycle P/L if the
  // currently-open CC gets assigned.  Captures the operator's full
  // accumulated edge (every premium collected since CSP open) net
  // of the strike differential between the current covered call
  // and the original CSP strike.
  //
  //   wound_per_share = credits_received_total + (kc_open - kp)
  //                   = kc_open - cost_basis_live       (algebra)
  //
  //   wound_dollars   = wound_per_share * 100 * contracts
  //                                                (Iter114.8)
  //
  // The 100 multiplier is the option-contract multiplier; the
  // `contracts` field (Iter114.8) scales the dollar total to the
  // operator's real position size (defaults to 1 for back-compat).
  const openCcLeg = ccLegs.find(l => (l.outcome || "open") === "open") || null;
  const contracts = Math.max(1, parseInt(c.contracts) || 1);
  const woundPerShare = (c.status === "CC_OPEN" && openCcLeg && kp > 0 && creditsTotal != null)
    ? round2(creditsTotal + (openCcLeg.kc - kp))
    : null;
  const woundDollars = woundPerShare != null
    ? round2(woundPerShare * 100 * contracts)
    : null;
  // Iter114.6 — launcher is meaningful in CC_OPEN (scenario test
  // alternatives) AND AWAITING_CC (pick the next CC after the
  // previous one expired).  No live broker quote on closed cycles.
  const isPickableForCC = c.status === "CC_OPEN" || c.status === "AWAITING_CC";

  return (
    <div
      data-testid={`cycle-card-${c.id}`}
      className={`bg-[#121216] border rounded-md mb-4 overflow-hidden ${
        isClosed ? "border-[#26262B] opacity-80" : "border-[#26262B] hover:border-[#3F3F47]"
      } transition-colors`}
    >
      {/* Hero header */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#26262B] bg-gradient-to-r from-[#0E0E11] to-[#121216]">
        <div className="flex items-center gap-3 flex-wrap">
          <TickerLogo symbol={c.symbol} />
          <span data-testid={`cycle-card-symbol-${c.symbol}`} className="text-xl font-mono font-bold text-white">
            {c.symbol}
          </span>
          <span
            data-testid={`cycle-card-status-${c.status}`}
            className="text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border uppercase tracking-[0.14em]"
            style={{ color: palette.fg, borderColor: `${palette.fg}40`, backgroundColor: `${palette.fg}10` }}
          >
            {palette.label}
          </span>
          <span className="text-[10px] font-mono text-[#8A8A93]">
            Kp ${kp} · {daysIn}d in cycle · {fmtDate(c.created_at)}
            {c.closed_at ? ` — ${fmtDate(c.closed_at)}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {isClosed && plCycle != null && (
            <div className="text-right" data-testid={`cycle-card-final-pnl-${c.id}`}>
              <div className={`text-xl font-mono font-bold ${plCycle >= 0 ? "text-[#00FF88]" : "text-[#FF5577]"}`}>
                {plCycle >= 0 ? "+" : ""}{fmtMoney(plCycle)}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                ROI {fmtPct(roiCycle)}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => onDelete(c.id)}
            data-testid={`cycle-card-delete-${c.id}`}
            className="text-[#8A8A93] hover:text-[#FF5577] p-1.5"
            title="Delete cycle"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Bento KPI strip */}
      <div data-testid={`cycle-card-kpi-strip-${c.id}`} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 px-3 py-3 border-b border-[#26262B]">
        <KpiTile label="Kp" value={fmtMoney(kp)} />
        <KpiTile label="Pp" value={fmtMoney(pp)} />
        <KpiTile
          label="Cost basis (live)"
          value={fmtMoney(costBasisLive)}
          accent={(c.open_cc_premium || 0) > 0 ? "#00FF88" : "#FFB800"}
        />
        <KpiTile
          label="Credits total"
          value={fmtMoney(creditsTotal)}
          accent="#00FF88"
        />
        <KpiTile
          label={isClosed ? "Final P/L" : sharesHeld && plUnrealized != null ? "Unrealized P/L" : "P/L (live)"}
          value={isClosed ? fmtMoney(plCycle) : sharesHeld && plUnrealized != null ? fmtMoney(plUnrealized) : "—"}
          accent={
            (isClosed ? plCycle : plUnrealized) == null ? "#8A8A93"
            : (isClosed ? plCycle : plUnrealized) >= 0 ? "#00FF88" : "#FF5577"
          }
        />
        <KpiTile
          label={isClosed ? "ROI" : "Days"}
          value={isClosed ? fmtPct(roiCycle) : `${daysIn}d`}
        />
      </div>

      {/* Cost-basis chart + Recovery panel side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 px-3 py-3 border-b border-[#26262B]">
        <div className="lg:col-span-2">
          <CostBasisChart
            cycleId={c.id}
            events={events}
            currentSpot={currentSpot}
            kp={kp}
          />
        </div>
        <RecoveryPanel
          cycleId={c.id}
          symbol={c.symbol}
          currentSpot={currentSpot}
          costBasisLive={costBasisLive}
          capitalToRecover={capitalToRecover}
          recoveryPct={recoveryPct}
          plUnrealized={plUnrealized}
          sharesHeld={sharesHeld}
          liveSpotsAt={liveSpotsAt}
          isClosed={isClosed}
          woundPerShare={woundPerShare}
          woundDollars={woundDollars}
          openKc={openCcLeg?.kc}
          kp={kp}
          creditsTotal={creditsTotal}
          openCcPremium={openCcLeg?.pc}
          isPickableForCC={isPickableForCC}
          contracts={contracts}
        />
      </div>

      {/* CC legs table */}
      {ccLegs.length > 0 && (
        <div data-testid={`cycle-card-legs-${c.id}`} className="px-5 py-3 border-b border-[#26262B] bg-[#0A0A0D]">
          <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#8A8A93] mb-2">
            CC Legs ({ccLegs.length})
          </div>
          <div className="space-y-1">
            {ccLegs.map((leg, i) => (
              <div
                key={i}
                data-testid={`cycle-card-leg-${c.id}-${i}`}
                className="flex items-center gap-3 flex-wrap py-1 text-[11px] font-mono"
              >
                <span className="text-[#5F5F66] w-12">CC#{i+1}</span>
                <span className="text-white">Kc=${leg.kc} · Pc=${leg.pc?.toFixed(2)}</span>
                <span className="text-[#8A8A93]">
                  P/L if assigned ${leg.pl_if_assigned?.toFixed(2)} · ROI {leg.roi_if_assigned}%
                </span>
                {leg.roll_credit_received != null && (
                  <span className="text-[#A78BFA]">net roll ${leg.roll_credit_received?.toFixed(2)}</span>
                )}
                <span className="text-[#3F3F47] text-[10px]">
                  {fmtDate(leg.added_at)}{leg.closed_at ? ` — ${fmtDate(leg.closed_at)}` : ""}
                </span>
                <span
                  data-testid={`cycle-card-leg-outcome-${c.id}-${i}`}
                  className="text-[9px] px-1.5 py-0.5 rounded-sm border uppercase tracking-[0.14em]"
                  style={{
                    color: LEG_PALETTE[leg.outcome || "open"],
                    borderColor: `${LEG_PALETTE[leg.outcome || "open"]}40`,
                  }}
                >
                  {leg.outcome || "open"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action footer */}
      {!isClosed && (
        <div data-testid={`cycle-card-actions-${c.id}`} className="px-5 py-3 flex flex-wrap items-center gap-2">
          {c.status === "CSP_OPEN" && (
            <>
              <ActionBtn label="CSP Assigned" icon={CheckCircle2} accent="#FFB800" onClick={() => onAction(c.id, "csp_assigned")} testid={`cycle-action-csp-assigned-${c.id}`} />
              <ActionBtn label="CSP Expired" icon={CheckCircle2} accent="#00FF88" onClick={() => onAction(c.id, "csp_expired")} testid={`cycle-action-csp-expired-${c.id}`} />
            </>
          )}
          {c.status === "AWAITING_CC" && onAddCC && (
            <ActionBtn label="+ Add Covered Call" icon={Plus} accent="#22D3FF" onClick={onAddCC} testid={`cycle-action-add-cc-${c.id}`} />
          )}
          {c.status === "CC_OPEN" && (
            <>
              <ActionBtn label="Roll CC" icon={RotateCcw} accent="#A78BFA" onClick={onRoll} testid={`cycle-action-roll-cc-${c.id}`} />
              <ActionBtn label="CC Assigned" icon={CheckCircle2} accent="#00FF88" onClick={() => onAction(c.id, "cc_assigned")} testid={`cycle-action-cc-assigned-${c.id}`} />
              <ActionBtn label="CC Expired" icon={CheckCircle2} accent="#FFB800" onClick={() => onAction(c.id, "cc_expired")} testid={`cycle-action-cc-expired-${c.id}`} />
            </>
          )}
        </div>
      )}
      {isClosed && c.close_reason && (
        <div className="px-5 py-3 text-xs font-mono text-[#8A8A93] border-t border-[#26262B] bg-[#0A0A0D]">
          {c.close_reason}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Small components
// ────────────────────────────────────────────────────────────────────
function KpiTile({ label, value, accent = "#E5E5E7" }) {
  return (
    <div className="border border-[#26262B] bg-[#0A0A0D] rounded-sm px-3 py-2">
      <div className="text-[8px] font-mono uppercase tracking-[0.18em] text-[#5F5F66]">{label}</div>
      <div className="text-sm font-mono font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function ActionBtn({ label, icon: Icon, accent, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-sm border transition-colors"
      style={{ color: accent, borderColor: `${accent}40`, backgroundColor: `${accent}0d` }}
    >
      <Icon size={11} /> {label}
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────
// Cost-basis chart (pure SVG, no recharts dependency)
//
// Iter114.1 — fixes the "steady flat line" bug: Y-axis now
// auto-scales to the cost-basis variation only (not forced to
// include `0` or `Kp`).  The line is rendered as a STEP path
// (horizontal-then-vertical) since cost basis changes
// instantaneously at each event — diagonal interpolation was
// misleading.  Kp + spot reference lines render INSIDE the chart
// only if they fit the auto-scaled range; otherwise they appear
// as floating chips so they don't dominate the Y scale.
// ────────────────────────────────────────────────────────────────────
function CostBasisChart({ cycleId, events, currentSpot, kp }) {
  const w = 600, h = 180, padL = 56, padR = 16, padT = 24, padB = 38;
  const data = events.map((e, i) => ({ ...e, x: i }));
  const hasData = data.length >= 1;
  const cbValues = data.map(d => Number(d.cost_basis_after) || 0);
  // Y-scale uses ONLY the cost-basis data range so the variation
  // is visible.  Kp + spot are drawn as overlay markers but never
  // forced into the scale.
  let yLo = Math.min(...cbValues);
  let yHi = Math.max(...cbValues);
  const span = yHi - yLo;
  // Padding: 25 % of the data range, or $0.50 minimum so a flat
  // series still has a visible band.
  const yPad = Math.max(span * 0.25, 0.5);
  const y0 = yLo - yPad;
  const y1 = yHi + yPad;
  const xMax = Math.max(1, data.length - 1);
  const xScale = (i) => padL + (i / xMax) * (w - padL - padR);
  const yScale = (v) => (h - padB) - ((v - y0) / Math.max(0.0001, y1 - y0)) * (h - padT - padB);

  // Step-line path: horizontal hold at each y, then vertical drop
  // into the next event.  Cost basis is constant between events
  // (a discrete instrument), so this is the honest visualisation.
  let stepPath = "";
  let areaPath = "";
  if (hasData) {
    const startX = xScale(0), startY = yScale(cbValues[0]);
    stepPath = `M ${startX.toFixed(1)} ${startY.toFixed(1)}`;
    areaPath = `M ${startX.toFixed(1)} ${(h - padB).toFixed(1)} L ${startX.toFixed(1)} ${startY.toFixed(1)}`;
    for (let i = 1; i < data.length; i++) {
      const nx = xScale(i), ny = yScale(cbValues[i]);
      const py = yScale(cbValues[i - 1]);
      stepPath += ` L ${nx.toFixed(1)} ${py.toFixed(1)} L ${nx.toFixed(1)} ${ny.toFixed(1)}`;
      areaPath += ` L ${nx.toFixed(1)} ${py.toFixed(1)} L ${nx.toFixed(1)} ${ny.toFixed(1)}`;
    }
    const endX = xScale(data.length - 1);
    areaPath += ` L ${endX.toFixed(1)} ${(h - padB).toFixed(1)} Z`;
  }

  const kpInRange = kp > 0 && kp >= y0 && kp <= y1;
  const spotInRange = currentSpot != null && currentSpot >= y0 && currentSpot <= y1;
  const delta = hasData ? cbValues[cbValues.length - 1] - cbValues[0] : 0;
  const totalDrop = hasData ? cbValues[0] - cbValues[cbValues.length - 1] : 0;

  return (
    <div data-testid={`cost-basis-chart-${cycleId}`} className="border border-[#26262B] bg-[#0A0A0D] rounded-sm px-3 py-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#8A8A93] inline-flex items-center gap-1.5">
          <TrendingUp size={10} /> Cost basis · cycle timeline
        </div>
        <div className="flex items-center gap-2 text-[9px] font-mono">
          {hasData && (
            <span
              data-testid={`cost-basis-chart-${cycleId}-delta`}
              className={`px-1.5 py-0.5 rounded-sm border ${
                totalDrop >= 0
                  ? "text-[#00FF88] border-[#00FF88]/30 bg-[#00FF88]/5"
                  : "text-[#FF5577] border-[#FF5577]/30 bg-[#FF5577]/5"
              }`}
            >
              {totalDrop >= 0 ? "↓" : "↑"} ${Math.abs(totalDrop).toFixed(2)}
            </span>
          )}
          <span className="text-[#5F5F66]">
            {data.length} event{data.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      {/* Floating chips for out-of-range references — anchored above
          the chart so the reader still sees the strike + spot context
          even when they're far from the cost-basis band. */}
      {(!kpInRange && kp > 0) || (!spotInRange && currentSpot != null) ? (
        <div className="flex items-center gap-2 mb-1 text-[8px] font-mono uppercase tracking-[0.14em]">
          {!kpInRange && kp > 0 && (
            <span className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/30 text-[#FFB800] bg-[#FFB800]/5">
              Kp ${kp.toFixed(2)} (off-chart {kp > y1 ? "↑" : "↓"})
            </span>
          )}
          {!spotInRange && currentSpot != null && (
            <span className="px-1.5 py-0.5 rounded-sm border border-[#22D3FF]/30 text-[#22D3FF] bg-[#22D3FF]/5">
              spot ${currentSpot.toFixed(2)} (off-chart {currentSpot > y1 ? "↑" : "↓"})
            </span>
          )}
        </div>
      ) : null}
      {hasData ? (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[180px]" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id={`cbFill-${cycleId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00FF88" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#00FF88" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Y-axis grid (4 ticks) */}
          {[0, 0.33, 0.66, 1].map((t, idx) => {
            const v = y0 + t * (y1 - y0);
            return (
              <g key={idx}>
                <line x1={padL} y1={yScale(v)} x2={w - padR} y2={yScale(v)} stroke="#1F1F25" strokeWidth="0.4" />
                <text x={padL - 6} y={yScale(v) + 4} textAnchor="end" fill="#8A8A93" fontSize="11" fontFamily="monospace">${v.toFixed(2)}</text>
              </g>
            );
          })}
          {/* Kp reference line (only in range) */}
          {kpInRange && (
            <>
              <line x1={padL} y1={yScale(kp)} x2={w - padR} y2={yScale(kp)} stroke="#FFB800" strokeWidth="0.8" strokeDasharray="4 4" opacity="0.6" />
              <text x={w - padR - 4} y={yScale(kp) - 4} textAnchor="end" fill="#FFB800" fontSize="11" fontFamily="monospace">Kp ${kp.toFixed(2)}</text>
            </>
          )}
          {/* Current spot reference line (only in range) */}
          {spotInRange && (
            <>
              <line x1={padL} y1={yScale(currentSpot)} x2={w - padR} y2={yScale(currentSpot)} stroke="#22D3FF" strokeWidth="0.8" strokeDasharray="3 3" opacity="0.8" />
              <text x={w - padR - 4} y={yScale(currentSpot) - 4} textAnchor="end" fill="#22D3FF" fontSize="11" fontFamily="monospace">spot ${currentSpot.toFixed(2)}</text>
            </>
          )}
          {/* Area fill under the cost basis step path */}
          <path data-testid={`cost-basis-chart-${cycleId}-area`} d={areaPath} fill={`url(#cbFill-${cycleId})`} />
          {/* Step path */}
          <path data-testid={`cost-basis-chart-${cycleId}-line`} d={stepPath} fill="none" stroke="#00FF88" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {/* Event dots + labels */}
          {data.map((d, i) => (
            <g key={i}>
              <circle cx={xScale(i)} cy={yScale(cbValues[i])} r="4" fill="#00FF88" stroke="#08080A" strokeWidth="1.5" data-testid={`cost-basis-chart-${cycleId}-dot-${i}`} />
              <text x={xScale(i)} y={yScale(cbValues[i]) - 9} textAnchor="middle" fill="#E5E5E7" fontSize="11" fontFamily="monospace" fontWeight="bold">${cbValues[i].toFixed(2)}</text>
              <text x={xScale(i)} y={h - padB + 14} textAnchor="middle" fill="#8A8A93" fontSize="10" fontFamily="monospace">{d.event?.replace(/_/g, " ")}</text>
            </g>
          ))}
          {/* Iter114.2 — Cyan "you are here" dot at the right edge of
              the chart, anchored to the live-spot Y coordinate.  This
              makes the live broker quote visually pop against the
              cost-basis step path so the operator instantly sees
              whether they're above or below breakeven. */}
          {spotInRange && (
            <g>
              <circle
                cx={w - padR - 2}
                cy={yScale(currentSpot)}
                r="6"
                fill="#22D3FF"
                stroke="#08080A"
                strokeWidth="2"
                data-testid={`cost-basis-chart-${cycleId}-live-spot-dot`}
              />
              <circle
                cx={w - padR - 2}
                cy={yScale(currentSpot)}
                r="10"
                fill="none"
                stroke="#22D3FF"
                strokeWidth="0.8"
                opacity="0.4"
              >
                <animate attributeName="r" values="6;12;6" dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;0;0.6" dur="2.4s" repeatCount="indefinite" />
              </circle>
            </g>
          )}
          {/* Net delta annotation in bottom-right corner */}
          {data.length >= 2 && (
            <text
              x={w - padR}
              y={h - 4}
              textAnchor="end"
              fill={totalDrop >= 0 ? "#00FF88" : "#FF5577"}
              fontSize="10"
              fontFamily="monospace"
            >
              Δ ${delta >= 0 ? "+" : ""}{delta.toFixed(2)} over {data.length} events
            </text>
          )}
        </svg>
      ) : (
        <div className="h-[180px] flex items-center justify-center text-[10px] font-mono text-[#5F5F66]">
          No events yet
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Recovery panel — Iter114.2 read-only redesign
// ────────────────────────────────────────────────────────────────────
// No inputs, no Save, no Pull-live button.  The live spot is now
// auto-fetched at the page level (`fetchLiveSpotsFor`) and pushed in
// as a prop.  This panel just renders the resulting recovery /
// unrealized-P/L summary in pure read-only form.
//
// Iter114.3 — adds "The Wound" block surfacing the projected full
// cycle P/L if the currently-open CC gets assigned, with the
// per-share + per-1-contract breakdown the operator can reason
// about at a glance.
function RecoveryPanel({
  cycleId, symbol, currentSpot, costBasisLive, capitalToRecover,
  recoveryPct, plUnrealized, sharesHeld, liveSpotsAt, isClosed,
  woundPerShare, woundDollars, openKc, kp, creditsTotal,
  openCcPremium, isPickableForCC, contracts = 1,
}) {
  const underwater = capitalToRecover != null && capitalToRecover > 0;
  const hasSpot = currentSpot != null;
  const hasWound = woundPerShare != null && woundDollars != null;
  // Iter114.4 — What-if Wound scenario test launcher state.
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  // For the "what-if I had picked a different Kc instead of the
  // currently-open CC", we strip the OPEN leg's premium out of
  // `credits_received_total` so the chain endpoint's `pl_cycle`
  // column = the hypothetical Wound per share if THIS candidate
  // strike replaced the current open CC.
  const whatIfPPrev = creditsTotal != null
    ? round2((creditsTotal || 0) - (openCcPremium || 0))
    : null;
  return (
    <div data-testid={`recovery-panel-${cycleId}`} className="border border-[#26262B] bg-[#0A0A0D] rounded-sm px-3 py-2">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#8A8A93] inline-flex items-center gap-1.5">
          <Crosshair size={10} /> {symbol} spot · recovery
        </div>
        {hasSpot && (
          <span
            data-testid={`recovery-panel-live-badge-${cycleId}`}
            className="text-[8px] font-mono uppercase tracking-[0.14em] text-[#22D3FF] border border-[#22D3FF]/30 bg-[#22D3FF]/5 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#22D3FF] animate-pulse" /> live
          </span>
        )}
      </div>
      {hasSpot ? (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">Spot</span>
            <span
              data-testid={`recovery-panel-spot-${cycleId}`}
              className="text-base font-mono font-bold text-[#22D3FF]"
            >
              {fmtMoney(currentSpot)}
            </span>
          </div>
          {costBasisLive != null && (
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">Breakeven</span>
              <span className="text-xs font-mono text-[#E5E5E7]">{fmtMoney(costBasisLive)}</span>
            </div>
          )}
          {sharesHeld && plUnrealized != null && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">Unrealized P/L</span>
              <span
                data-testid={`recovery-panel-unrealized-${cycleId}`}
                className={`text-xs font-mono font-bold ${plUnrealized >= 0 ? "text-[#00FF88]" : "text-[#FF5577]"}`}
              >
                {plUnrealized >= 0 ? "+" : ""}{fmtMoney(plUnrealized)}
              </span>
            </div>
          )}
          {underwater ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#FFB800] inline-flex items-center gap-1">
                  <AlertTriangle size={10} /> Capital to recover
                </span>
                <span data-testid={`recovery-panel-capital-${cycleId}`} className="text-xs font-mono font-bold text-[#FFB800]">
                  {fmtMoney(capitalToRecover)} / share
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">Recovery to BE</span>
                <span data-testid={`recovery-panel-pct-${cycleId}`} className="text-xs font-mono font-bold text-[#FFB800]">
                  +{fmtPct(recoveryPct)}
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-[#00FF88]">
              <Activity size={10} /> Above cost basis · no recovery needed
            </div>
          )}
          {liveSpotsAt && (
            <div className="pt-1 mt-1 border-t border-[#1F1F25] text-[8px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
              auto-pulled · {new Date(liveSpotsAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      ) : (
        <div
          data-testid={`recovery-panel-no-spot-${cycleId}`}
          className="text-[10px] font-mono text-[#5F5F66]"
        >
          {isClosed
            ? "Cycle closed — live spot not pulled"
            : "Waiting for live broker quote…"}
        </div>
      )}

      {/* Iter114.3 — "The Wound" block.  Renders only when a CC is
          open (per operator spec) so we have a concrete Kc to project
          against.  Shows the full premium ledger + strike differential
          + final per-contract P/L if assigned. */}
      {hasWound && (
        <div
          data-testid={`recovery-panel-wound-${cycleId}`}
          className="mt-3 pt-3 border-t border-[#26262B]"
        >
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#A78BFA] inline-flex items-center gap-1.5">
              <AlertTriangle size={10} /> Wound · cycle P/L if CC assigned
            </span>
            <span
              data-testid={`recovery-panel-wound-total-${cycleId}`}
              className={`text-lg font-mono font-black ${woundDollars >= 0 ? "text-[#00FF88]" : "text-[#FF5577]"}`}
            >
              {woundDollars >= 0 ? "+" : ""}{fmtMoney(woundDollars)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono">
            <span className="text-[#8A8A93]">Total premiums</span>
            <span data-testid={`recovery-panel-wound-premiums-${cycleId}`} className="text-right text-[#00FF88]">
              {fmtMoney(creditsTotal)} / sh
            </span>
            <span className="text-[#8A8A93]">Kc<sub className="text-[8px]">open</sub> − Kp</span>
            <span data-testid={`recovery-panel-wound-strike-diff-${cycleId}`} className={`text-right ${(openKc - kp) >= 0 ? "text-[#00FF88]" : "text-[#FF5577]"}`}>
              {(openKc - kp) >= 0 ? "+" : ""}{fmtMoney(openKc - kp)} / sh
            </span>
            <span className="text-[#8A8A93]">Per share</span>
            <span
              data-testid={`recovery-panel-wound-per-share-${cycleId}`}
              className={`text-right font-bold ${woundPerShare >= 0 ? "text-[#00FF88]" : "text-[#FF5577]"}`}
            >
              {woundPerShare >= 0 ? "+" : ""}{fmtMoney(woundPerShare)}
            </span>
          </div>
          <div
            data-testid={`recovery-panel-wound-contracts-${cycleId}`}
            className="mt-2 text-[8px] font-mono uppercase tracking-[0.14em] text-[#FFB800]/80 leading-snug"
          >
            ⓘ Per {contracts} contract{contracts === 1 ? "" : "s"} ({contracts * 100} share{contracts * 100 === 1 ? "" : "s"}).
            {contracts === 1 && " Open a multi-contract cycle to auto-scale."}
          </div>
        </div>
      )}
      {/* Iter114.4 — What-if Wound scenario test launcher.  Opens
          the shared `CCChainDiagModal` (Dashboard's CC chain
          diagnostics) pre-filled with this cycle's context and
          auto-fetches the live chain so the operator can
          scenario-test alternative Kc strikes without leaving
          the Cycle Tracker.
          Iter114.6 — Now also renders in AWAITING_CC so the
          operator can pick the NEXT CC strike right after the
          previous one expired (decision-support framing rather
          than post-trade regret framing). */}
      {isPickableForCC && whatIfPPrev != null && (
        <button
          type="button"
          data-testid={`recovery-panel-whatif-launcher-${cycleId}`}
          onClick={() => setWhatIfOpen(true)}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-[0.14em] text-[#A78BFA] border border-[#A78BFA]/30 hover:border-[#A78BFA]/60 hover:bg-[#A78BFA]/5 px-3 py-2 rounded-sm transition-colors"
          title="Load the live option chain to scenario-test CC strikes within ±2σ of spot"
        >
          <FlaskConical size={11} />
          {hasWound
            ? "Try a different strike · live chain"
            : "Pick next CC strike · live chain"}
        </button>
      )}
      {/* Iter114.4 — Modal lives at panel scope so click-away on the
          backdrop closes it without unmounting the card.
          Iter114.5 — also receives `currentWoundPerShare` so the
          modal can paint a delta badge per row + filter to
          "improvements only".
          Iter114.6 — filter evolved to "Tradeable opportunities"
          (±2σ + Wheel ITM doctrine + beats-current when applicable). */}
      {whatIfOpen && whatIfPPrev != null && (
        <CCChainDiagModal
          symbol={symbol}
          autoFetch={true}
          currentWoundPerShare={woundPerShare}
          contracts={contracts}
          cycleData={{
            kp: kp,
            p_prev: whatIfPPrev,
            // Iter114.7 — kc_open lets the modal derive the
            // current-leg mid-price from the same chain payload
            // and surface an "Order ticket preview" with the
            // implied net roll credit (`new_pc_mid −
            // current_cc_mid`).  Null on AWAITING_CC since there
            // is no leg to buy back.
            kc_open: openKc,
            // dte_p = 0 → Step 1 (`dte_total ≤ 20`) reduces to a
            // pure call-side DTE filter, which is what the
            // operator actually cares about when picking a
            // candidate CC strike.
            dte_p: 0,
          }}
          onClose={() => setWhatIfOpen(false)}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Modals
// ────────────────────────────────────────────────────────────────────
function NewCycleModal({ onClose, onSaved }) {
  const [symbol, setSymbol] = useState("");
  const [kp, setKp] = useState("");
  const [pp, setPp] = useState("");
  const [dte, setDte] = useState("");
  const [spotEntry, setSpotEntry] = useState("");
  // Iter114.8 — Contract count (default 1).  Scales `capital_invested`
  // backend-side and all $-totals client-side (Wound, Net Roll).
  const [contracts, setContracts] = useState("1");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!symbol || !kp || !pp || !dte || !spotEntry) return;
    const contractsN = Math.max(1, parseInt(contracts) || 1);
    setSaving(true);
    try {
      await axios.post(`${API}/cycles`, {
        symbol: symbol.toUpperCase(),
        kp: parseFloat(kp),
        pp: parseFloat(pp),
        dte_put: parseInt(dte),
        spot_entry: parseFloat(spotEntry),
        contracts: contractsN,
        comment,
      });
      toast.success(`Cycle opened: ${symbol.toUpperCase()} K${kp}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open cycle");
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={onClose}
      data-testid="new-cycle-modal-overlay"
    >
      <div
        className="bg-[#121216] border border-[#26262B] rounded-md w-[520px] max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="new-cycle-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#26262B]">
          <h3 className="text-lg font-bold text-white font-['Chivo']">Open New Cycle</h3>
          <button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1.5 block">Symbol</label>
            <input
              type="text"
              value={symbol}
              onChange={e => setSymbol(e.target.value.toUpperCase())}
              placeholder="e.g. AAPL"
              data-testid="new-cycle-symbol-input"
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2.5 text-sm font-mono outline-none uppercase"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <ModalField label="Kp (CSP strike)" value={kp} onChange={setKp} step="0.5" testid="new-cycle-kp-input" />
            <ModalField label="Pp (premium)" value={pp} onChange={setPp} step="0.01" testid="new-cycle-pp-input" />
            <ModalField label="DTE put" value={dte} onChange={setDte} step="1" testid="new-cycle-dte-input" />
          </div>
          <ModalField label="Spot at entry" value={spotEntry} onChange={setSpotEntry} step="0.01" testid="new-cycle-spot-input" />
          {/* Iter114.8 — Contract count selector.  Defaults to 1.
              Drives `capital_invested` backend-side and the $-total
              scaling for Wound / Net Roll client-side. */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1.5 block">
              Contracts <span className="text-[#5F5F66] normal-case lowercase">(1 contract = 100 shares)</span>
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={contracts}
              onChange={e => setContracts(e.target.value)}
              data-testid="new-cycle-contracts-input"
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2.5 text-sm font-mono outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1.5 block">Note (optional)</label>
            <input
              type="text"
              value={comment}
              onChange={e => setComment(e.target.value)}
              data-testid="new-cycle-comment-input"
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2.5 text-xs font-mono outline-none"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !symbol || !kp || !pp || !dte || !spotEntry}
            data-testid="new-cycle-save-btn"
            className="w-full bg-[#00FF88] text-[#08080A] font-bold py-2.5 rounded-sm text-sm disabled:opacity-40"
          >
            {saving ? "Saving…" : "Open Cycle"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RollCCModal({ cycleId, onClose, onSaved }) {
  const [kc, setKc] = useState("");
  const [pc, setPc] = useState("");
  const [dte, setDte] = useState("");
  const [credit, setCredit] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!kc || !pc || !dte || credit === "") return;
    setSaving(true);
    try {
      await axios.post(`${API}/cycles/${cycleId}/cc/roll`, {
        kc: parseFloat(kc),
        pc: parseFloat(pc),
        dte_call: parseInt(dte),
        roll_credit_received: parseFloat(credit),
        note,
      });
      toast.success(`Roll recorded · K${kc}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Roll failed");
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={onClose}
      data-testid="roll-cc-modal-overlay"
    >
      <div
        className="bg-[#121216] border border-[#26262B] rounded-md w-[520px] max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="roll-cc-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#26262B]">
          <h3 className="text-lg font-bold text-white font-['Chivo'] inline-flex items-center gap-2">
            <RotateCcw size={16} className="text-[#A78BFA]" /> Roll Covered Call
          </h3>
          <button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] font-mono text-[#8A8A93]">
            Matches the Tasty / Tradier order-replace shape. The current open leg auto-closes as <span className="text-[#A78BFA]">rolled</span>, then a new open leg is appended with the parameters below.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <ModalField label="New Kc" value={kc} onChange={setKc} step="0.5" testid="roll-cc-kc-input" />
            <ModalField label="New Pc" value={pc} onChange={setPc} step="0.01" testid="roll-cc-pc-input" />
            <ModalField label="New DTE" value={dte} onChange={setDte} step="1" testid="roll-cc-dte-input" />
          </div>
          <ModalField label="Net credit received" value={credit} onChange={setCredit} step="0.01" testid="roll-cc-credit-input" />
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1.5 block">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              data-testid="roll-cc-note-input"
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#A78BFA] text-white rounded-sm px-3 py-2.5 text-xs font-mono outline-none"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !kc || !pc || !dte || credit === ""}
            data-testid="roll-cc-save-btn"
            className="w-full bg-[#A78BFA] text-[#08080A] font-bold py-2.5 rounded-sm text-sm disabled:opacity-40"
          >
            {saving ? "Rolling…" : "Roll the CC"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, step, testid }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1.5 block">{label}</label>
      <input
        type="number"
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2.5 text-sm font-mono outline-none"
      />
    </div>
  );
}
